import { useState, useEffect, useCallback } from "react";
import {
  createReminder as createReminderAPI,
  getReminders as getRemindersAPI,
  cancelReminder as cancelReminderAPI,
} from "../services/api";

export function useReminders() {
  const [reminders, setReminders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isCancelling, setIsCancelling] = useState(null); // holds the id being cancelled

  const fetchReminders = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getRemindersAPI();
      setReminders(res.data.reminders || []);
    } catch {
      // Silently fail — component can show error via empty state
      setReminders([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const createReminder = useCallback(async ({ message, company, role, targetPerson }) => {
    setIsCreating(true);
    try {
      const res = await createReminderAPI({ message, company, role, targetPerson });
      // Refresh reminders list after creating
      fetchReminders();
      return { success: true, data: res.data };
    } catch (err) {
      if (err.response?.status === 409) {
        return { success: false, duplicate: true, data: err.response.data };
      }
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [fetchReminders]);

  const cancelReminder = useCallback(async (id) => {
    setIsCancelling(id);
    // Optimistic removal
    const prev = reminders;
    setReminders((r) => r.filter((rem) => rem._id !== id));
    try {
      await cancelReminderAPI(id);
      return { success: true };
    } catch (err) {
      // Restore on failure
      setReminders(prev);
      throw err;
    } finally {
      setIsCancelling(null);
    }
  }, [reminders]);

  return {
    reminders,
    isLoading,
    isCreating,
    isCancelling,
    fetchReminders,
    createReminder,
    cancelReminder,
  };
}
